// src/app/services/insights-api-service.ts
import { apiFetch } from '@/api/api-fetch';
import type {
  SavedInsight, InsightCreatePayload,
  GeneratedReport, ReportGenerateRequest,
} from '../../components/reporting/types';

// ── Insights ──────────────────────────────────────────────────────────────────

export async function fetchInsights(brandName: string): Promise<SavedInsight[]> {
  const res = await apiFetch<{ insights: SavedInsight[]; total: number }>(
    `/insights?brand_name=${encodeURIComponent(brandName)}`
  );
  return res.insights;
}

export async function saveInsight(payload: InsightCreatePayload): Promise<SavedInsight> {
  return apiFetch<SavedInsight>('/insights', { method: 'POST', body: JSON.stringify(payload) });
}

export async function deleteInsight(insightId: number): Promise<void> {
  await apiFetch(`/insights/${insightId}`, { method: 'DELETE' });
}

/**
 * Check the DB to see if an insight with this title already exists for the
 * given brand. Returns { exists, insight_id }.
 * Used by InsightActionButton on mount for deduplication without localStorage.
 */
export async function checkInsightExists(
  brandName: string,
  title: string,
): Promise<{ exists: boolean; insight_id: number | null }> {
  return apiFetch<{ exists: boolean; insight_id: number | null }>(
    `/insights/check?brand_name=${encodeURIComponent(brandName)}&title=${encodeURIComponent(title)}`
  );
}

// ── Reports ───────────────────────────────────────────────────────────────────

export async function generateReport(payload: ReportGenerateRequest): Promise<GeneratedReport> {
  return apiFetch<GeneratedReport>('/insights/reports/generate', {
    method: 'POST', body: JSON.stringify(payload),
  });
}

export async function updateReport(
  reportId: number,
  output: {
    title: string; audience: string; format: string; confidence: number;
    executive_summary: string; sections: unknown[];
    key_findings: string[]; recommendations: string[]; insight_count: number;
  }
): Promise<GeneratedReport> {
  return apiFetch<GeneratedReport>(`/insights/reports/${reportId}`, {
    method: 'PUT', body: JSON.stringify({ output }),
  });
}

export async function fetchReports(brandName: string): Promise<GeneratedReport[]> {
  const res = await apiFetch<{ reports: GeneratedReport[]; total: number }>(
    `/insights/reports?brand_name=${encodeURIComponent(brandName)}`
  );
  return res.reports;
}

export async function createShareLink(
  reportId: number
): Promise<{ token: string; url: string; expiresAt: string }> {
  // Share links are public and time-limited; expires_at tells the sharer when
  // the link they are about to send stops working.
  const { token, expires_at } = await apiFetch<{ token: string; expires_at: string }>(
    `/insights/reports/${reportId}/share`, { method: 'POST' }
  );
  const apiBase = (window as any).__API_BASE__ || '';
  const url = `${apiBase}/api/insights/reports/shared/${token}`;
  return { token, url, expiresAt: expires_at };
}

export async function revokeShareLink(reportId: number): Promise<boolean> {
  const { revoked } = await apiFetch<{ revoked: boolean }>(
    `/insights/reports/${reportId}/share`, { method: 'DELETE' }
  );
  return revoked;
}

export interface ShareLinkSettings {
  ttl_days: number;
  min_ttl_days: number;
  max_ttl_days: number;
}

/** How long share links stay valid, platform-wide. Readable by any user (the
 *  share dialog shows it); only admins may change it. */
export async function fetchShareLinkSettings(): Promise<ShareLinkSettings> {
  return apiFetch<ShareLinkSettings>('/insights/share-link-settings');
}

export async function updateShareLinkSettings(
  ttlDays: number
): Promise<ShareLinkSettings> {
  return apiFetch<ShareLinkSettings>('/insights/share-link-settings', {
    method: 'PUT',
    body: JSON.stringify({ ttl_days: ttlDays }),
  });
}

export async function fetchSharedReport(token: string): Promise<GeneratedReport> {
  return apiFetch<GeneratedReport>(`/insights/reports/shared/${token}`);
}